import type {
  SortDirection,
  SortOption,
} from "@client/hooks/use-my-evaluations-data";
import { cn } from "@client/utils/cn";
import { Checkbox, TableHead, TableHeader, TableRow } from "@sfc/ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getProposalColumnClass } from "./proposals-table-layout";

const sortableHeaderClass =
  "flex cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 -mx-1 -my-0.5 text-label-md font-medium text-text-secondary transition-colors hover:text-accent outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

function SortableTableHead({
  label,
  sortKey,
  currentSort,
  sortDirection,
  onSort,
  className,
  align = "left",
}: {
  label: string;
  sortKey: SortOption;
  currentSort: SortOption;
  sortDirection: SortDirection;
  onSort: (key: SortOption) => void;
  className?: string;
  align?: "left" | "center" | "right";
}) {
  const isActive = currentSort === sortKey;
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";
  const buttonJustify =
    align === "right"
      ? "justify-end w-full"
      : align === "center"
        ? "justify-center w-full"
        : "";
  return (
    <TableHead className={cn("text-text-secondary", alignClass, className)}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          sortableHeaderClass,
          buttonJustify,
          isActive && "text-accent",
        )}
      >
        {label}
        {isActive ? (
          sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        )}
      </button>
    </TableHead>
  );
}

export function ProposalsTableHeader({
  isRecommender,
  isObserver = false,
  sort,
  sortDirection,
  onSortChange,
  canHide = false,
  allEligibleSelected = false,
  onToggleSelectAll,
  searchActive = false,
  showTracks = true,
}: {
  isRecommender: boolean;
  isObserver?: boolean;
  sort: SortOption;
  sortDirection: SortDirection;
  onSortChange: (sort: SortOption) => void;
  canHide?: boolean;
  allEligibleSelected?: boolean;
  onToggleSelectAll?: () => void;
  searchActive?: boolean;
  // Trackless rounds hide the Tracks column entirely.
  showTracks?: boolean;
}) {
  const proposalColumnClass = getProposalColumnClass({
    searchActive,
    isRecommender,
  });

  return (
    <TableHeader className="bg-surface-base">
      <TableRow className="border-border-subtle">
        {canHide && (
          <TableHead className="w-10 px-2 py-3">
            <Checkbox
              checked={allEligibleSelected}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all"
            />
          </TableHead>
        )}
        <SortableTableHead
          label="Proposal Name"
          sortKey="org-name"
          currentSort={sort}
          sortDirection={sortDirection}
          onSort={onSortChange}
          className={proposalColumnClass}
        />
        {showTracks && (
          <TableHead className="w-44 min-w-36 text-label-md font-medium text-text-secondary px-4 py-3">
            Tracks
          </TableHead>
        )}
        <SortableTableHead
          label="Base Request"
          sortKey="base-request"
          currentSort={sort}
          sortDirection={sortDirection}
          onSort={onSortChange}
          align="right"
          className="w-32 min-w-28"
        />
        {isObserver && (
          <SortableTableHead
            label="Notes"
            sortKey="notes"
            currentSort={sort}
            sortDirection={sortDirection}
            onSort={onSortChange}
            align="center"
          />
        )}
        {isRecommender && (
          <SortableTableHead
            label="Investigation Interest"
            sortKey="investigation-interest"
            currentSort={sort}
            sortDirection={sortDirection}
            onSort={onSortChange}
            align="center"
          />
        )}
        {isRecommender && (
          <SortableTableHead
            label="Review Progress"
            sortKey="review-progress"
            currentSort={sort}
            sortDirection={sortDirection}
            onSort={onSortChange}
            align="center"
          />
        )}
        {isRecommender && (
          <SortableTableHead
            label="Status"
            sortKey="status"
            currentSort={sort}
            sortDirection={sortDirection}
            onSort={onSortChange}
            align="center"
          />
        )}
        {isRecommender && (
          <SortableTableHead
            label="Last Touched"
            sortKey="last-touched"
            currentSort={sort}
            sortDirection={sortDirection}
            onSort={onSortChange}
            align="right"
          />
        )}
        {isRecommender && (
          <TableHead
            className="w-10 px-2 py-3 text-label-md font-medium text-text-secondary"
            aria-label="Actions"
          />
        )}
      </TableRow>
    </TableHeader>
  );
}
